import { useState, useEffect } from "react";
import { parroquiasAPI } from "@/lib/api";
import { Info, Edit, Save, X, MapPin, Phone, Mail, Clock, Church } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

export default function InfoGeneralPage() {
  const [parroquia, setParroquia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    direccion: "",
    telefono: "",
    email: "",
    parroco: "",
    diocesis: "",
    horarios_misa: "",
    territorio: "",
  });

  useEffect(() => {
    loadParroquia();
  }, []);

  const loadParroquia = async () => {
    try {
      setLoading(true);
      const response = await parroquiasAPI.getAll();
      if (response.data.length > 0) {
        const p = response.data[0];
        setParroquia(p);
        setFormData({
          nombre: p.nombre || "",
          direccion: p.direccion || "",
          telefono: p.telefono || "",
          email: p.email || "",
          parroco: p.parroco || "",
          diocesis: p.diocesis || "",
          horarios_misa: p.horarios_misa || "",
          territorio: p.territorio || "",
        });
      }
    } catch (error) {
      console.error("Error loading parroquia:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      if (parroquia) {
        await parroquiasAPI.update(parroquia.id, formData);
        toast.success("Información actualizada correctamente");
      } else {
        const response = await parroquiasAPI.create(formData);
        setParroquia(response.data);
        toast.success("Información guardada correctamente");
      }
      setEditing(false);
      loadParroquia();
    } catch (error) {
      toast.error("Error al guardar la información");
    }
  };

  const handleCancel = () => {
    if (parroquia) {
      setFormData({
        nombre: parroquia.nombre || "",
        direccion: parroquia.direccion || "",
        telefono: parroquia.telefono || "",
        email: parroquia.email || "",
        parroco: parroquia.parroco || "",
        diocesis: parroquia.diocesis || "",
        horarios_misa: parroquia.horarios_misa || "",
        territorio: parroquia.territorio || "",
      });
    }
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-stone-400">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="info-general-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <Info className="w-8 h-8 text-stone-600" strokeWidth={1.5} />
            Información General
          </h1>
          <p className="text-stone-600 mt-1">Datos de la parroquia y horarios</p>
        </div>
        {!editing ? (
          <Button
            onClick={() => setEditing(true)}
            variant="outline"
            className="h-10"
            data-testid="editar-info-btn"
          >
            <Edit className="w-4 h-4 mr-2" />
            Editar
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button onClick={handleCancel} variant="outline" className="h-10">
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              className="h-10 bg-rose-950 hover:bg-rose-900 text-white"
              data-testid="guardar-info-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </div>
        )}
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Datos Principales */}
        <Card className="border-stone-200 bg-white md:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
              <Church className="w-5 h-5 text-rose-700" />
              Datos de la Parroquia
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {editing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre de la Parroquia *</Label>
                  <Input
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="diocesis">Diócesis *</Label>
                  <Input
                    id="diocesis"
                    name="diocesis"
                    value={formData.diocesis}
                    onChange={handleChange}
                    required
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="parroco">Párroco</Label>
                  <Input
                    id="parroco"
                    name="parroco"
                    value={formData.parroco}
                    onChange={handleChange}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="territorio">Territorio Parroquial</Label>
                  <Textarea
                    id="territorio"
                    name="territorio"
                    value={formData.territorio}
                    onChange={handleChange}
                    rows={2}
                    className="resize-none"
                    placeholder="Barrios, sectores o áreas que cubre la parroquia..."
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Nombre</p>
                  <p className="text-xl font-serif text-stone-800 mt-1">
                    {parroquia?.nombre || "Sin configurar"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Diócesis</p>
                  <p className="text-stone-800 mt-1">
                    {parroquia?.diocesis || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Párroco</p>
                  <p className="text-stone-800 mt-1">
                    {parroquia?.parroco || "—"}
                  </p>
                </div>
                {parroquia?.territorio && (
                  <div className="md:col-span-2">
                    <p className="text-xs text-stone-500 uppercase tracking-wide">Territorio Parroquial</p>
                    <p className="text-stone-600 mt-1">{parroquia.territorio}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contacto */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
              <Phone className="w-5 h-5 text-emerald-600" />
              Contacto
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {editing ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="direccion">Dirección</Label>
                  <Input
                    id="direccion"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleChange}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Correo Electrónico</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="h-11"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-stone-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wide">Dirección</p>
                    <p className="text-stone-800 mt-1">{parroquia?.direccion || "—"}</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-stone-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wide">Teléfono</p>
                    <p className="text-stone-800 mt-1">{parroquia?.telefono || "—"}</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-stone-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wide">Correo</p>
                    <p className="text-stone-800 mt-1">{parroquia?.email || "—"}</p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Horarios */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Horarios de Misa
            </CardTitle>
          </CardHeader>
          <CardContent>
            {editing ? (
              <div className="space-y-2">
                <Label htmlFor="horarios_misa">Horarios</Label>
                <Textarea
                  id="horarios_misa"
                  name="horarios_misa"
                  value={formData.horarios_misa}
                  onChange={handleChange}
                  rows={6}
                  className="resize-none"
                  placeholder="Lunes a Viernes: 7:00 AM, 6:00 PM&#10;Sábados: 6:00 PM&#10;Domingos: 7:00 AM, 9:00 AM, 11:00 AM, 6:00 PM"
                />
              </div>
            ) : (
              <div className="whitespace-pre-wrap text-stone-700">
                {parroquia?.horarios_misa || (
                  <p className="text-stone-500">No hay horarios configurados</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Help Info */}
      <Card className="border-stone-200 bg-stone-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-stone-200">
              <Info className="w-5 h-5 text-stone-600" />
            </div>
            <div>
              <h3 className="font-medium text-stone-800">Información</h3>
              <p className="text-sm text-stone-600 mt-1">
                Esta información se utiliza para generar los documentos oficiales de la parroquia
                (actas de bautizo, matrimonio, confirmación). Asegúrate de mantenerla actualizada.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
